import { defineStore } from "pinia";
import { useAuthStore } from "./auth";
import axios from "axios";
import moment from "moment";

let inFlightOrgId = null;
let inFlightOrgPromise = null;
let lastFetchedOrgId = null;
let lastFetchedOrgTime = 0;

export const useOrganisationStore = defineStore("organisation", {
  persist: true,
  state: () => ({
    temp_org: {},
    organisation: {},
    organisationNextBillingDate: "",
    organisationCoupon: {
      is_credit_card_required: true,
    },
    organisationPricingPlans: [],
    integrations: [],
  }),

  actions: {
    async getOrganisation(id, forceRefresh = false) {
      const authStore = useAuthStore();
      if (!id) {
        console.warn("getOrganisation: No organisation ID provided.");
        return;
      }

      const normalizedId = Number(id);
      const now = Date.now();

      if (
        !forceRefresh &&
        this.organisation?.id === normalizedId &&
        lastFetchedOrgId === normalizedId &&
        now - lastFetchedOrgTime < 1000
      ) {
        return;
      }

      if (!forceRefresh && inFlightOrgPromise && inFlightOrgId === normalizedId) {
        return await inFlightOrgPromise;
      }

      inFlightOrgId = normalizedId;
      inFlightOrgPromise = (async () => {
        try {
          const [resp, billingResp] = await Promise.all([
            axios.get(`/orgadmin/organisations/${id}`),
            axios
              .get(`/orgadmin/organisations/${id}/next-billing-date`)
              .catch((err) => {
                console.error(err);
                return null;
              }),
          ]);
          if (!resp.data.success) {
            this.$toast.error("Error fetching organisation");
            return;
          }
          this.organisation = resp.data.organisation;
          lastFetchedOrgId = normalizedId;
          lastFetchedOrgTime = Date.now();
          await this.getNextBillingDate(id, billingResp);

          if (!authStore.isSuperAdmin) {
            if (this.organisation.org_status == "inactive") {
              authStore.logout();
            }
          }
        } catch (error) {
          console.error("Error fetching single organisation:", error);
        } finally {
          if (inFlightOrgId === normalizedId) {
            inFlightOrgId = null;
            inFlightOrgPromise = null;
          }
        }
      })();

      return await inFlightOrgPromise;
    },

    async getNextBillingDate(orgId = null, preloadedResp = null) {
      const activeOrgId = orgId || this.organisation?.id;
      if (!activeOrgId) {
        return;
      }

      try {
        const resp =
          preloadedResp ||
          (await axios.get(
            `/orgadmin/organisations/${activeOrgId}/next-billing-date`
          ));
        if (!resp || !resp.data) {
          return;
        }

        if (!resp.data.success) {
          console.error(resp.data.error || resp.data.message || "Failed to fetch next billing date");
          return;
        }

        if (resp.data.next_billing_date) {
          this.organisationNextBillingDate = moment.utc(
            resp.data.next_billing_date
          ).format("LL");
        } else if (resp.data.next_billing_month && resp.data.billing_day) {
          let billingDay = resp.data.billing_day || 1;
          if (billingDay > 28) {
            billingDay = 28;
          }

          if (this.organisation?.billing_date) {
            const baseDate = moment.utc(this.organisation.billing_date);
            const nextBillingDate = baseDate
              .clone()
              .add(1, "months")
              .date(billingDay);
            this.organisationNextBillingDate = nextBillingDate.format("LL");
          } else {
            const now = moment();
            const currentMonth = now.month() + 1;
            let year = now.year();
            if (resp.data.next_billing_month < currentMonth) {
              year += 1;
            }
            this.organisationNextBillingDate = moment({
              year: year,
              month: resp.data.next_billing_month - 1,
              date: billingDay,
            }).format("LL");
          }
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getOrganisationActiveCoupons(id) {
      try {
        const resp = await axios.get(`/orgadmin/organisations/${id}/get-active-organisation-coupon`)
        if (resp.data.success) {
          this.organisationCoupon = resp.data.organisation_coupons
        } else {
          this.$toast.error("error fetching organisation coupons")
         
        }
      } catch (err) {
        console.error(err)
      }
    },

    setOrganisationCoupon(organisation_coupon) {
      this.organisationCoupon = organisation_coupon;
    },

    setTemporaryOrg(temp_org) {
      this.temp_org = temp_org;
    },

    async getIntegrations(orgId) {
      const integrations = [];

      // 1. Fetch BambooHR Integration
      try {
        const bambooResp = await axios.get(`/orgadmin/organisations/${orgId}/bamboo-hr`);
        if (bambooResp.data.success) {
          integrations.push({
            id: 1,
            name: "BambooHR Integration",
            enabled: true,
            apiKey: bambooResp.data.bamboo_hr_api_key,
            subDomain: bambooResp.data.bamboo_hr_subdomain,
          });
        } else {
          integrations.push({
            id: 1,
            name: "BambooHR Integration",
            enabled: false,
          });
        }
      } catch (error) {
        console.error("Error fetching BambooHR integration:", error);
        integrations.push({
          id: 1,
          name: "BambooHR Integration",
          enabled: false,
        });
      }

      // 2. Fetch Xero Integration
      try {
        const xeroResp = await axios.get(`/orgadmin/organisations/${orgId}/xero-integration`);
        if (xeroResp.data.success) {
          const contactId = xeroResp.data.xero_contact_id;
          const hasContactId = !!(contactId && String(contactId).trim() !== '');
          integrations.push({
            id: 2,
            name: "Xero Integration",
            enabled: hasContactId,
            xero_contact_id: xeroResp.data.xero_contact_id,
            sync_status: xeroResp.data.sync_status,
            last_sync: xeroResp.data.last_sync,
            has_xero_conflict: xeroResp.data.has_xero_conflict,
            last_error: xeroResp.data.last_error,
            latest_job_status: xeroResp.data.latest_job_status,
            latest_job_id: xeroResp.data.latest_job_id,
          });
        } else {
          integrations.push({
            id: 2,
            name: "Xero Integration",
            enabled: false,
          });
        }
      } catch (error) {
        console.error("Error fetching Xero integration:", error);
        integrations.push({
          id: 2,
          name: "Xero Integration",
          enabled: false,
        });
      }

      this.integrations = integrations;
    },

    async getBambooHr(orgId) {
      return this.getIntegrations(orgId);
    },

    async saveBambooHr(orgId, data) {
      try {
        const response = await axios.post(
          `/orgadmin/organisations/${orgId}/bamboo-hr`,
          data
        );
        if (response.data.success) {
          await this.getBambooHr(orgId);
          return { success: true, message: response.data.message };
        } else {
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        console.error(error);
        return { success: false, message: "An error occurred while saving." };
      }
    },

    async saveXeroIntegration(orgId, data) {
      try {
        const response = await axios.put(
          `/orgadmin/organisations/${orgId}/xero-integration`,
          data
        );
        if (response.data.success) {
          await this.getIntegrations(orgId);
          return { success: true, message: response.data.message };
        } else {
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        console.error(error);
        return { success: false, message: "An error occurred while saving." };
      }
    },

    async resolveXeroSyncConflict(orgId, sourceOfTruth) {
      try {
        const response = await axios.post(
          `/supadmin/organisations/${orgId}/resolve-sync`,
          { source: sourceOfTruth }
        );
        if (response.data.success) {
          await this.getIntegrations(orgId);
          await this.getOrganisation(orgId);
          return { success: true, message: response.data.message };
        } else {
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        console.error("Error resolving sync conflict:", error);
        return { success: false, message: "An error occurred while resolving conflict" };
      }
    },

    async disableBambooHr(orgId) {
      try {
        const response = await axios.get(
          `/orgadmin/organisations/${orgId}/bamboo-hr/disable`
        );
        if (response.data.success) {
          await this.getBambooHr(orgId);
          return { success: true, message: response.data.message };
        } else {
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        console.error(error);
        return { success: false, message: "An error occurred while disabling." };
      }
    },

    async getXeroSyncJobs(orgId, page = 1, limit = 5) {
      try {
        const response = await axios.get(`/supadmin/xero/jobs`, {
          params: { organisation_id: orgId, page, limit }
        });
        if (response.data.success) {
          return response.data;
        }
        return { success: false, jobs: [], total: 0 };
      } catch (error) {
        console.error("Error fetching Xero sync jobs:", error);
        return { success: false, jobs: [], total: 0 };
      }
    },

    async retryXeroSyncJob(jobId) {
      try {
        const response = await axios.post(`/supadmin/xero/jobs/${jobId}/retry`);
        return response.data;
      } catch (error) {
        console.error("Error retrying Xero sync job:", error);
        return { success: false, message: error.response?.data?.error || "Failed to retry job" };
      }
    },

    async getXeroActivityLogs(orgId) {
      try {
        const response = await axios.get(`/orgadmin/organisations/${orgId}/xero-activity-logs`);
        if (response.data.success) {
          return response.data.activity_logs || [];
        }
        return [];
      } catch (error) {
        console.error("Error fetching Xero activity logs:", error);
        return [];
      }
    },

    async getOrganisationActivityLogs(orgId, params = {}) {
      if (!orgId) return [];
      try {
        const response = await axios.get(
          `/orgadmin/organisations/${orgId}/activity-logs`,
          { params }
        );
        if (response.data.success) {
          return response.data.activity_logs || [];
        }
        return [];
      } catch (error) {
        console.error("Error fetching organisation activity logs:", error);
        return [];
      }
    },
  },
});
